<template>
  <f7-page name="campaign-list">
    <f7-navbar :title="$t('campaign.campaigns')" back-link />
    <AppLayout>
      <template #main>
        <f7-block class="list-header">
          <f7-button
            fill raised round
            @click="createCampaign()"
           
          >
            <f7-icon f7="plus" size="14" />
            {{ $t("campaign.addNewCapaign") }}
          </f7-button>
        </f7-block>

        <f7-block v-if="loading" class="loading-block">
          <f7-preloader />
        </f7-block>

        <f7-block v-else-if="campaigns.length === 0" class="empty-block">
          <CtaMessageCard
            variant="catalyst"
            icon="megaphone_fill"
            :title="$t('cta.campaign.title')"
            :description="$t('cta.campaign.desc')"
            :button-label="$t('cta.campaign.cta')"
            @action="createCampaign()"
          />
        </f7-block>

        <div v-else class="campaign-grid grid grid-cols-1">
          <span width="100" v-for="c in campaigns" :key="c.id">
            <a class="campaign-card-link" @click="goToCampaign(c)">
              <f7-card class="campaign-card">
                <f7-card-content>
                  <div class="cc-head">
                    <span class="cc-type">
                      <f7-icon
                        :f7="metaFor(c.type).icon"
                        size="18"
                        :color="typeColor(c.type)"
                      />
                      {{ metaFor(c.type).label }}
                    </span>
                    <span
                      class="campaign-status-pill"
                      :class="statusClass(c.status)"
                    >
                      {{ statusLabel(c.status) }}
                    </span>
                  </div>
                  <div class="cc-name">{{ c.name }}</div>
                  <div v-if="campaignGoalLabel(c)" class="cc-goal">
                    {{ campaignGoalLabel(c) }}
                  </div>
                  <div v-if="campaignProgress(c) != null" class="cc-progress">
                    <div
                      class="cc-progress-fill"
                      :style="{ width: campaignProgress(c) + '%' }"
                    ></div>
                    <div class="cc-progress-label">
                      {{ campaignProgress(c) }}%
                    </div>
                  </div>
                  <div v-if="c.status === 'DRAFT'" class="cc-draft-badge">
                    {{ $t('campaign.lifecycle.DRAFT') }}
                  </div>
                </f7-card-content>
              </f7-card>
            </a>
          </span>
        </div>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import CtaMessageCard from "../components/home/CtaMessageCard.vue";
import { metaFor } from "../constants/campaign-capabilities.js";
import { useCampaignsStore } from "../js/pinia-stores/campaign.js";
import i18n from "../js/i18n.js";
const props = defineProps({
  groupId: { type: String, required: true },
  f7route: Object,
  f7router: Object,
});

const campaignsStore = useCampaignsStore();
const campaigns = ref([]);
const loading = ref(true);
const { t } = i18n.global;
const statusLabels = {
  DRAFT: t("campaign.lifecycle.DRAFT"),
  SETUP_REQUIRED: t("campaign.lifecycle.SETUP_REQUIRED"),
  READY_TO_PUBLISH: t("campaign.lifecycle.READY_TO_PUBLISH"),
  PENDING_VERIFICATION: t("campaign.lifecycle.PENDING_VERIFICATION"),
  ACTIVE: t("campaign.lifecycle.ACTIVE"),
  PAUSED: t("campaign.lifecycle.PAUSED"),
  COMPLETED: t("campaign.lifecycle.COMPLETED"),
  FAILED: t("campaign.lifecycle.FAILED"),
  CANCELLED: t("campaign.lifecycle.CANCELLED"),
  EXPIRED: t("campaign.lifecycle.EXPIRED"),
  ARCHIVED: t("campaign.lifecycle.ARCHIVED"),
};
function statusLabel(s) {
  return statusLabels[s] || s;
}
function statusClass(s) {
  const map = {
    DRAFT: 'status-draft',
    SETUP_REQUIRED: 'status-setup',
    READY_TO_PUBLISH: 'status-ready',
    PENDING_VERIFICATION: 'status-verification',
    ACTIVE: 'status-active',
    PAUSED: 'status-paused',
    COMPLETED: 'status-completed',
    FAILED: 'status-failed',
    CANCELLED: 'status-cancelled',
    EXPIRED: 'status-expired',
    ARCHIVED: 'status-archived',
  };
  return map[s] || 'status-unknown';
}

function campaignGoalLabel(c) {
  if (c.targetAmountCfa)
    return `${Math.round(c.currentAmountCfa ?? 0).toLocaleString(
      "fr-FR",
    )} / ${c.targetAmountCfa.toLocaleString("fr-FR")} ${c.currency}`;
  if (c.targetQuantity)
    return `${c.currentQuantity ?? 0} / ${c.targetQuantity} ${t(
      "common.units",
    )}`;
  if (c.maxParticipants)
    return `${c.currentParticipants ?? 0} / ${c.maxParticipants} ${t(
      "common.places",
    )}`;
  return "";
}

const TYPE_COLORS = {
  GROUP_BUY: "#1a6b42",
  PREORDER: "#1a6b42",
  COOP_PURCHASE: "#1a6b42",
  PRIVATE_SHOP: "#1a6b42",
  GROUP_SAVINGS: "#0e7490",
  TONTINE: "#0e7490",
  SUBSCRIPTION: "#0e7490",
  FUND_COLLECT: "#185fa5",
  DONATION_DRIVE: "#185fa5",
  CHARITY: "#185fa5",
  FUNDRAISER: "#185fa5",
  COMMUNITY_PROJECT: "#185fa5",
  SPONSORSHIP: "#185fa5",
  TICKETING: "#a08520",
  EVENT_REGISTRATION: "#a08520",
  RSVP: "#a08520",
  RAFFLE: "#a08520",
  MEMBERSHIP: "#6b46c1",
  AUCTION: "#6b46c1",
  AD_CAMPAIGN: "#a32d2d",
};
function typeColor(type) {
  return TYPE_COLORS[type] || "#8e8e93";
}

function campaignProgress(c) {
  if (c.targetAmountCfa)
    return Math.min(
      100,
      Math.round(((c.currentAmountCfa ?? 0) / c.targetAmountCfa) * 100),
    );
  if (c.targetQuantity)
    return Math.min(
      100,
      Math.round(((c.currentQuantity ?? 0) / c.targetQuantity) * 100),
    );
  if (c.maxParticipants)
    return Math.min(
      100,
      Math.round(((c.currentParticipants ?? 0) / c.maxParticipants) * 100),
    );
  return null;
}
function createCampaign(){
  f7.views.main.router.navigate(`/groups/${props.groupId}/campaigns/create`)
}
function goTocampaign(c){
  f7.views.main.router.navigate(`/groups/${props.groupId}/campaigns/${c.id}`)
}

onMounted(async () => {
  campaigns.value = await campaignsStore.fetchByGroup(props.groupId);
  loading.value = false;
});
</script>

<style scoped>
.loading-block {
  text-align: center;
  padding: 30px 0;
}
.empty-block {
  text-align: center;
  padding: 40px 20px;
}
.list-header {
  padding-bottom: 0;
}
.campaign-grid {
  margin: 0 16px;
}
.campaign-card-link {
  display: block;
  color: inherit;
}
.campaign-card {
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
  margin: 0;
}
.cc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.cc-type {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
}
.cc-name {
  font-size: 16px;
  font-weight: 600;
  margin-top: 6px;
}
.cc-goal {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 4px;
}
.cc-progress {
  height: 6px;
  border-radius: 4px;
  background: var(--f7-list-item-border-color, #e0e0e0);
  margin-top: 10px;
  overflow: hidden;
  position: relative;
}
.cc-progress-fill {
  height: 100%;
  border-radius: 4px;
  background: var(--f7-theme-color);
  transition: width 0.3s ease;
}
.cc-progress-label {
  position: absolute;
  right: 8px;
  top: -18px;
  font-size: 10px;
  font-weight: 600;
  color: var(--f7-theme-color);
}
.campaign-status-pill {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.campaign-status-pill.status-draft {
  background: color-mix(in srgb, #8e8e93 12%, transparent);
  color: var(--f7-on-surface-variant, #8e8e93);
}
.campaign-status-pill.status-setup {
  background: color-mix(in srgb, #a08520 12%, transparent);
  color: #a08520;
}
.campaign-status-pill.status-ready {
  background: color-mix(in srgb, #0e7490 12%, transparent);
  color: #0e7490;
}
.campaign-status-pill.status-verification {
  background: color-mix(in srgb, #a08520 12%, transparent);
  color: #a08520;
}
.campaign-status-pill.status-active {
  background: color-mix(in srgb, #1a6b42 12%, transparent);
  color: #1a6b42;
}
.campaign-status-pill.status-paused {
  background: color-mix(in srgb, #a08520 12%, transparent);
  color: #a08520;
}
.campaign-status-pill.status-completed {
  background: color-mix(in srgb, #185fa5 12%, transparent);
  color: #185fa5;
}
.campaign-status-pill.status-failed,
.campaign-status-pill.status-cancelled,
.campaign-status-pill.status-expired {
  background: color-mix(in srgb, #772e37 12%, transparent);
  color: #772e37;
}
.campaign-status-pill.status-archived {
  background: color-mix(in srgb, #8e8e93 12%, transparent);
  color: var(--f7-on-surface-variant, #8e8e93);
}
.cc-draft-badge {
  margin-top: 8px;
  font-size: 10px;
  font-weight: 600;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>
